import React, { useState, useEffect } from 'react';
import { X, Edit, Trash2, Users, Mail, Calendar, MapPin, Clock, AlertCircle } from 'lucide-react';
import moment from 'moment';
import adminCalendarService from '../../services/adminCalendarService';
import CreateAgencyEventModal from './CreateAgencyEventModal';

const AgencyEventDetailsModal = ({ event, onClose, onEventUpdated, onEventDeleted }) => {
  const [responses, setResponses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);

  const eventData = event.resource || event;

  useEffect(() => {
    loadResponses();
  }, [event]);

  const loadResponses = async () => {
    try {
      setLoading(true);
      const data = await adminCalendarService.getEventResponses(eventData.id);
      setResponses(data);
    } catch (error) {
      console.error('Error loading responses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${eventData.title}"? This will remove it from all agents' calendars.`)) {
      return;
    }

    try {
      await adminCalendarService.deleteEvent(eventData.id);
      onEventDeleted();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    }
  };

  const handleSendReminder = async () => {
    if (!window.confirm('Send reminder email to all agents who haven\'t responded?')) {
      return;
    }

    setSendingReminder(true);
    try {
      // TODO: Implement reminder endpoint in backend
      // await adminCalendarService.sendEventReminder(eventData.id);
      alert('Reminder feature coming soon! Backend endpoint needs to be implemented.');
    } catch (error) {
      console.error('Error sending reminder:', error);
      alert('Failed to send reminder. Please try again.');
    } finally {
      setSendingReminder(false);
    }
  };

  const getEventTypeLabel = (type) => {
    const labels = {
      'agency_event': 'Agency Event',
      'training': 'Training',
      'meeting': 'Meeting',
      'deadline': 'Deadline'
    };
    return labels[type] || type;
  };

  const getEventTypeColor = (type) => {
    const colors = {
      'agency_event': 'bg-red-100 text-red-800',
      'training': 'bg-orange-100 text-orange-800',
      'meeting': 'bg-gray-100 text-gray-800',
      'deadline': 'bg-yellow-100 text-yellow-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b px-6 py-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getEventTypeColor(eventData.event_type)}`}>
                    {getEventTypeLabel(eventData.event_type)}
                  </span>
                  {eventData.is_mandatory && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Mandatory
                    </span>
                  )}
                </div>
                <h3 className="text-xl font-semibold text-gray-900">{eventData.title}</h3>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Event Details */}
            <div className="space-y-4">
              {eventData.description && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Description</h4>
                  <p className="text-gray-900">{eventData.description}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Date</h4>
                    <p className="text-gray-900">
                      {moment(event.start).format('MMMM D, YYYY')}
                      {eventData.end_date && eventData.end_date !== eventData.start_date && 
                        ` - ${moment(eventData.end_date).format('MMMM D, YYYY')}`}
                    </p>
                  </div>
                </div>

                {!eventData.all_day && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Time</h4>
                      <p className="text-gray-900">
                        {moment(event.start).format('h:mm A')} - {moment(event.end).format('h:mm A')}
                      </p>
                    </div>
                  </div>
                )}

                {eventData.location && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Location</h4>
                      <p className="text-gray-900">{eventData.location}</p>
                    </div>
                  </div>
                )}

                {eventData.created_by_name && (
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-gray-500">Created By</h4>
                      <p className="text-gray-900">{eventData.created_by_name}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Response Summary */}
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : responses && (
              <div className="border-t pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-gray-900">Agent Responses</h4>
                  {responses.summary.pending > 0 && (
                    <button
                      onClick={handleSendReminder}
                      disabled={sendingReminder}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                    >
                      <Mail className="w-4 h-4" />
                      {sendingReminder ? 'Sending...' : 'Send Reminder'}
                    </button>
                  )}
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-gray-900">{responses.summary.total_invited}</div>
                    <div className="text-sm text-gray-600">Total Invited</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-700">{responses.summary.accepted}</div>
                    <div className="text-sm text-green-600">Accepted</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-yellow-700">{responses.summary.tentative}</div>
                    <div className="text-sm text-yellow-600">Tentative</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-red-700">{responses.summary.declined}</div>
                    <div className="text-sm text-red-600">Declined</div>
                  </div>
                </div>

                {/* Detailed Responses */}
                <div className="space-y-4">
                  {/* Accepted */}
                  {responses.responses.accepted.length > 0 && (
                    <div>
                      <h5 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        Accepted ({responses.responses.accepted.length})
                      </h5>
                      <div className="bg-green-50 rounded-lg p-3 space-y-1">
                        {responses.responses.accepted.map((agent) => (
                          <div key={agent.agent_id} className="text-sm text-gray-700">
                            {agent.name} ({agent.email})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Tentative */}
                  {responses.responses.tentative.length > 0 && (
                    <div>
                      <h5 className="font-medium text-yellow-700 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                        Tentative ({responses.responses.tentative.length})
                      </h5>
                      <div className="bg-yellow-50 rounded-lg p-3 space-y-1">
                        {responses.responses.tentative.map((agent) => (
                          <div key={agent.agent_id} className="text-sm text-gray-700">
                            {agent.name} ({agent.email})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Declined */}
                  {responses.responses.declined.length > 0 && (
                    <div>
                      <h5 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                        Declined ({responses.responses.declined.length})
                      </h5>
                      <div className="bg-red-50 rounded-lg p-3 space-y-1">
                        {responses.responses.declined.map((agent) => (
                          <div key={agent.agent_id} className="text-sm text-gray-700">
                            {agent.name} ({agent.email})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pending */}
                  {responses.responses.pending.length > 0 && (
                    <div>
                      <h5 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                        Pending ({responses.responses.pending.length})
                      </h5>
                      <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                        {responses.responses.pending.map((agent) => (
                          <div key={agent.agent_id} className="text-sm text-gray-700">
                            {agent.name} ({agent.email})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-between">
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete Event
            </button>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Edit className="w-4 h-4" />
                Edit Event
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <CreateAgencyEventModal
          editEvent={event}
          onClose={() => setShowEditModal(false)}
          onEventCreated={() => {
            setShowEditModal(false);
            onEventUpdated();
          }}
        />
      )}
    </>
  );
};

export default AgencyEventDetailsModal;
